import type { ScreenSchema } from './types';

/**
 * Generic intro-only schema for screens whose only audible content is the
 * standard uc-content block (heading → text → instructions).
 *
 * Used for:
 *   - Screens with no `template` field set (the v-else default in uc-screen)
 *   - `image`      — uses +template-wrapper which includes +content
 *
 * Excluded (container templates whose own child screens manage their own audio):
 *   - `quiz`  — screen-quiz creates its own uc-scroll-section per question; registering
 *   - `bank`    quiz/bank in contentSchema causes the parent screen's sectionActive to fire
 *               simultaneously with the first question's, creating a race that overwrites
 *               the question's JiT audio. These templates have no schema so the parent
 *               uc-screen's watcher exits early and questions play cleanly.
 *
 * Excluded (have their own schemas):
 *   - `image-list`         → screen-image-list.schema.ts
 *   - `big-quote`          → screen-big-quote.schema.ts
 *   - `hotspot-fullscreen` → screen-hotspot-fullscreen.schema.ts
 *   - `sticky-left/right`  → screen-sticky.schema.ts
 *   - `split/split-2-1/split-1-2` → screen-split.schema.ts (managesOwnSectionAudio —
 *                             sequences parent intro then left/right child intros)
 *   - `slides`             → screen-slides.schema.ts
 *   - `assessment`         → screen-assessment.schema.ts (managesOwnSectionAudio)
 *   - `result`             → screen-result.schema.ts (managesOwnSectionAudio — sequences
 *                             intro then result child screens as a coordinated chain)
 *
 * Excluded (no audible text content):
 *   - `full-width-image` — renders only an <img>
 *   - `brushstroke`      — renders only the screen-brush SVG
 *   - `video`            — video autoplays; narration would conflict
 *   - `lightbox-video`   — same reasoning as video
 */
const contentSchema: ScreenSchema = {
  template: ['content', 'text', 'image'],
  slots: {
    intro: {
      parts: [
        { field: 'heading', fallback: ['subheading', 'title'] },
        { field: 'text' },
        { field: 'instructions' }
      ]
    }
  }
};

export default contentSchema;
